import React, { useEffect, useState, useRef, useCallback } from "react";
import { msalInstance, msalBereit, msLoginRedirect } from "../msalClient";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GOOGLE_SCOPE = "https://www.googleapis.com/auth/calendar";
const G_TOKEN_KEY = "kalender_google_token";
const G_TOKEN_ABLAUF_KEY = "kalender_google_token_ablauf";
const AUSWAHL_KEY = "kalender_auswahl";
const MS_ACCOUNT_KEY = "kalender_ms_account_id";

const MS_SCOPES = ["User.Read", "Calendars.ReadWrite"];

function heuteISO() {
  return new Date().toISOString().slice(0, 10);
}

function gespeichertenGoogleTokenLaden() {
  const ablauf = Number(localStorage.getItem(G_TOKEN_ABLAUF_KEY) || 0);
  if (ablauf > Date.now()) return localStorage.getItem(G_TOKEN_KEY);
  return null;
}

function gespeicherteAuswahlLaden() {
  try { return JSON.parse(localStorage.getItem(AUSWAHL_KEY) || "{}"); } catch { return {}; }
}

// Komposit-IDs verhindern Kollisionen zwischen Google- und Outlook-Kalender-IDs
const kompId = (quelle, rawId) => `${quelle}:${rawId}`;
const teileKompId = (kompositId) => {
  const idx = kompositId.indexOf(":");
  return { quelle: kompositId.slice(0, idx), rawId: kompositId.slice(idx + 1) };
};

export default function Kalender() {
  const [googleToken, setGoogleToken] = useState(gespeichertenGoogleTokenLaden());
  const [msToken, setMsToken] = useState(null);
  const [msAccount, setMsAccount] = useState(null);
  const [kalenderListe, setKalenderListe] = useState([]);
  const [ausgewaehlteKalender, setAusgewaehlteKalender] = useState({});
  const [termine, setTermine] = useState([]);
  const [ladeFehler, setLadeFehler] = useState("");
  const [formOffen, setFormOffen] = useState(false);
  const [bearbeitenTermin, setBearbeitenTermin] = useState(null);
  const [formular, setFormular] = useState(leeresFormular());
  const googleTokenClientRef = useRef(null);
  const heuteRef = useRef([]);
  const containerRef = useRef(null);
  const stickyRef = useRef(null);

  function leeresFormular() {
    return { titel: "", beschreibung: "", ort: "", datum: heuteISO(), startZeit: "09:00", endZeit: "10:00", kalenderId: "" };
  }

  // ── Google: Token-Client initialisieren ──────────────────────────────────────
  useEffect(() => {
    const warten = setInterval(() => {
      if (window.google?.accounts?.oauth2) {
        clearInterval(warten);
        googleTokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: GOOGLE_SCOPE,
          callback: (antwort) => {
            if (antwort.error) { setLadeFehler("Google-Autorisierung fehlgeschlagen: " + antwort.error); return; }
            const ablaufMs = Date.now() + (Number(antwort.expires_in) || 3600) * 1000 - 60000;
            localStorage.setItem(G_TOKEN_KEY, antwort.access_token);
            localStorage.setItem(G_TOKEN_ABLAUF_KEY, String(ablaufMs));
            setGoogleToken(antwort.access_token);
          },
        });
        // Stille Erneuerung: wenn Token abgelaufen, aber Nutzer hat sich schon mal verbunden
        if (!gespeichertenGoogleTokenLaden() && localStorage.getItem(G_TOKEN_ABLAUF_KEY)) {
          googleTokenClientRef.current.requestAccessToken({ prompt: "" });
        }
      }
    }, 200);
    return () => clearInterval(warten);
  }, []);

  // ── Google: Token automatisch erneuern, bevor er abläuft ─────────────────────
  useEffect(() => {
    if (!googleToken) return;
    const ablauf = Number(localStorage.getItem(G_TOKEN_ABLAUF_KEY) || 0);
    const warteMs = ablauf - Date.now() - 5 * 60 * 1000; // 5 min vor Ablauf erneuern
    if (warteMs <= 0) return;
    const timer = setTimeout(() => {
      if (googleTokenClientRef.current) {
        googleTokenClientRef.current.requestAccessToken({ prompt: "" });
      }
    }, warteMs);
    return () => clearTimeout(timer);
  }, [googleToken]);

  const googleVerbinden = () => {
    if (!googleTokenClientRef.current) { alert("Google-Anmeldung lädt noch, bitte kurz warten."); return; }
    googleTokenClientRef.current.requestAccessToken();
  };

  const googleTrennen = () => {
    localStorage.removeItem(G_TOKEN_KEY);
    localStorage.removeItem(G_TOKEN_ABLAUF_KEY);
    setGoogleToken(null);
  };

  // ── Outlook: MSAL initialisieren + stille Anmeldung versuchen ───────────────
  useEffect(() => {
    (async () => {
      await msalBereit;
      const gespeicherteId = localStorage.getItem(MS_ACCOUNT_KEY);
      let konto = gespeicherteId ? msalInstance.getAccount({ homeAccountId: gespeicherteId }) : null;
      // Fallback: alle gecachten Konten durchsuchen
      if (!konto) {
        const alleKonten = msalInstance.getAllAccounts();
        if (alleKonten.length > 0) konto = alleKonten[0];
      }
      if (konto) {
        try {
          const ergebnis = await msalInstance.acquireTokenSilent({ scopes: MS_SCOPES, account: konto });
          setMsAccount(ergebnis.account);
          setMsToken(ergebnis.accessToken);
          localStorage.setItem(MS_ACCOUNT_KEY, ergebnis.account.homeAccountId);
        } catch (err) {
          console.warn("[MSAL Kalender] Stille Anmeldung fehlgeschlagen:", err.message);
        }
      }
    })();
  }, []);

  const outlookVerbinden = async () => {
    try {
      await msLoginRedirect(MS_SCOPES, "kalender");
    } catch (err) { setLadeFehler("Outlook-Autorisierung fehlgeschlagen: " + err.message); }
  };

  const outlookTrennen = () => {
    localStorage.removeItem(MS_ACCOUNT_KEY);
    setMsAccount(null);
    setMsToken(null);
  };

  // ── Kalenderliste (Google + Outlook zusammengeführt) ─────────────────────────
  const kalenderListeLaden = useCallback(async () => {
    const ergebnisse = [];
    if (googleToken) {
      try {
        const res = await fetch("https://www.googleapis.com/calendar/v3/users/me/calendarList", {
          headers: { Authorization: `Bearer ${googleToken}` },
        });
        const daten = await res.json();
        if (daten.error) {
          if (daten.error.code === 401) { googleTrennen(); }
          else setLadeFehler(daten.error.message);
        } else {
          (daten.items || []).forEach((k) => {
            ergebnisse.push({ id: kompId("google", k.id), summary: k.summary, backgroundColor: k.backgroundColor || "#1a73e8", quelle: "google" });
          });
        }
      } catch (err) { setLadeFehler(err.message); }
    }
    if (msToken) {
      try {
        const res = await fetch("https://graph.microsoft.com/v1.0/me/calendars", {
          headers: { Authorization: `Bearer ${msToken}` },
        });
        const daten = await res.json();
        if (daten.error) { setLadeFehler(daten.error.message); }
        else {
          (daten.value || []).forEach((k) => {
            ergebnisse.push({ id: kompId("outlook", k.id), summary: k.name, backgroundColor: "#0078d4", quelle: "outlook" });
          });
        }
      } catch (err) { setLadeFehler(err.message); }
    }
    setKalenderListe(ergebnisse);
    const gespeichert = gespeicherteAuswahlLaden();
    const auswahl = {};
    ergebnisse.forEach((k) => { auswahl[k.id] = k.id in gespeichert ? gespeichert[k.id] : true; });
    setAusgewaehlteKalender(auswahl);
  }, [googleToken, msToken]);

  useEffect(() => { kalenderListeLaden(); }, [kalenderListeLaden]);

  // ── Termine laden (beide Quellen) ────────────────────────────────────────────
  const termineLaden = useCallback(async () => {
    if (kalenderListe.length === 0) { setTermine([]); return; }
    setLadeFehler("");
    const jetzt = new Date();
    const von = new Date(jetzt.getFullYear(), jetzt.getMonth() - 1, 1);
    const bis = new Date(jetzt.getFullYear(), jetzt.getMonth() + 2, 0);
    const aktive = kalenderListe.filter((k) => ausgewaehlteKalender[k.id]);
    try {
      const alle = await Promise.all(
        aktive.map(async (k) => {
          const { quelle, rawId } = teileKompId(k.id);
          if (quelle === "google") {
            const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(rawId)}/events?timeMin=${von.toISOString()}&timeMax=${bis.toISOString()}&singleEvents=true&orderBy=startTime`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${googleToken}` } });
            const daten = await res.json();
            if (daten.error) return [];
            return (daten.items || []).map((e) => ({
              id: e.id, summary: e.summary, description: e.description, location: e.location,
              start: e.start, end: e.end,
              kalenderId: k.id, kalenderFarbe: k.backgroundColor, kalenderName: k.summary, quelle: "google",
            }));
          } else {
            const url = `https://graph.microsoft.com/v1.0/me/calendars/${rawId}/calendarView?startDateTime=${von.toISOString()}&endDateTime=${bis.toISOString()}&$orderby=start/dateTime`;
            const res = await fetch(url, {
              headers: { Authorization: `Bearer ${msToken}`, Prefer: 'outlook.timezone="Europe/Berlin"' },
            });
            const daten = await res.json();
            if (daten.error) return [];
            return (daten.value || []).map((e) => ({
              id: e.id, summary: e.subject, description: e.bodyPreview, location: e.location?.displayName,
              start: { dateTime: e.start?.dateTime }, end: { dateTime: e.end?.dateTime },
              kalenderId: k.id, kalenderFarbe: k.backgroundColor, kalenderName: k.summary, quelle: "outlook",
            }));
          }
        })
      );
      const zusammengefuehrt = alle.flat().sort((a, b) => {
        const da = a.start?.dateTime || a.start?.date;
        const db_ = b.start?.dateTime || b.start?.date;
        return new Date(da) - new Date(db_);
      });
      setTermine(zusammengefuehrt);
    } catch (err) { setLadeFehler(err.message); }
  }, [kalenderListe, ausgewaehlteKalender, googleToken, msToken]);

  useEffect(() => { termineLaden(); }, [termineLaden]);

  // Springt zum nächsten anstehenden Termin direkt unter das fest stehende Menü.
  const zuJetztSpringen = useCallback(() => {
    if (termine.length === 0) return;
    const jetzt = new Date();
    const index = termine.findIndex((t) => new Date(t.start?.dateTime || t.start?.date) >= jetzt);
    const zielIndex = index === -1 ? termine.length - 1 : index;
    const el = heuteRef.current[zielIndex];
    const container = containerRef.current;
    if (!el || !container) return;
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    container.scrollTop += elRect.top - containerRect.top - 8;
  }, [termine]);

  useEffect(() => {
    if (termine.length === 0) return;
    setTimeout(zuJetztSpringen, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termine]);

  const kalenderToggle = (id) => {
    setAusgewaehlteKalender((prev) => {
      const neu = { ...prev, [id]: !prev[id] };
      localStorage.setItem(AUSWAHL_KEY, JSON.stringify(neu));
      return neu;
    });
  };

  const neuerTermin = () => {
    setFormular({ ...leeresFormular(), kalenderId: kalenderListe[0]?.id || "" });
    setBearbeitenTermin(null);
    setFormOffen(true);
  };

  const terminBearbeiten = (t) => {
    const startDt = t.start?.dateTime ? new Date(t.start.dateTime) : null;
    const endDt = t.end?.dateTime ? new Date(t.end.dateTime) : null;
    setFormular({
      titel: t.summary || "",
      beschreibung: t.description || "",
      ort: t.location || "",
      datum: (t.start?.date || t.start?.dateTime || "").slice(0, 10),
      startZeit: startDt ? startDt.toTimeString().slice(0, 5) : "09:00",
      endZeit: endDt ? endDt.toTimeString().slice(0, 5) : "10:00",
      kalenderId: t.kalenderId,
    });
    setBearbeitenTermin(t);
    setFormOffen(true);
  };

  const terminSpeichern = async (e) => {
    e.preventDefault();
    if (!formular.titel.trim() || !formular.kalenderId) return;
    const istBearbeiten = !!bearbeitenTermin;
    const kompositId = istBearbeiten ? bearbeitenTermin.kalenderId : formular.kalenderId;
    const { quelle, rawId } = teileKompId(kompositId);
    try {
      if (quelle === "google") {
        const body = {
          summary: formular.titel, description: formular.beschreibung, location: formular.ort,
          start: { dateTime: `${formular.datum}T${formular.startZeit}:00`, timeZone: "Europe/Berlin" },
          end: { dateTime: `${formular.datum}T${formular.endZeit}:00`, timeZone: "Europe/Berlin" },
        };
        const url = istBearbeiten
          ? `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(rawId)}/events/${bearbeitenTermin.id}`
          : `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(rawId)}/events`;
        const res = await fetch(url, {
          method: istBearbeiten ? "PATCH" : "POST",
          headers: { Authorization: `Bearer ${googleToken}`, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const daten = await res.json();
        if (daten.error) { alert("Fehler: " + daten.error.message); return; }
      } else {
        const body = {
          subject: formular.titel,
          body: { contentType: "text", content: formular.beschreibung },
          location: { displayName: formular.ort },
          start: { dateTime: `${formular.datum}T${formular.startZeit}:00`, timeZone: "Europe/Berlin" },
          end: { dateTime: `${formular.datum}T${formular.endZeit}:00`, timeZone: "Europe/Berlin" },
        };
        const url = istBearbeiten
          ? `https://graph.microsoft.com/v1.0/me/calendars/${rawId}/events/${bearbeitenTermin.id}`
          : `https://graph.microsoft.com/v1.0/me/calendars/${rawId}/events`;
        const res = await fetch(url, {
          method: istBearbeiten ? "PATCH" : "POST",
          headers: { Authorization: `Bearer ${msToken}`, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) { const f = await res.json(); alert("Fehler: " + (f.error?.message || res.statusText)); return; }
      }
      setFormOffen(false);
      termineLaden();
    } catch (err) { alert("Fehler: " + err.message); }
  };

  const terminLoeschen = async (t) => {
    if (!window.confirm("Termin wirklich löschen?")) return;
    const { quelle, rawId } = teileKompId(t.kalenderId);
    try {
      if (quelle === "google") {
        await fetch(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(rawId)}/events/${t.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${googleToken}` },
        });
      } else {
        await fetch(`https://graph.microsoft.com/v1.0/me/calendars/${rawId}/events/${t.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${msToken}` },
        });
      }
      termineLaden();
    } catch (err) { alert("Fehler: " + err.message); }
  };

  const formatDatum = (t) => {
    const start = t.start?.dateTime || t.start?.date;
    const ganztag = !t.start?.dateTime;
    const d = new Date(start);
    const datumStr = d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
    if (ganztag) return `${datumStr} · ganztägig`;
    const zeitStr = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    return `${datumStr} · ${zeitStr}`;
  };

  // ── Kein Kalender verbunden ───────────────────────────────────────────────────
  if (!googleToken && !msToken) {
    return (
      <div style={styles.unverbundenSeite}>
        <h2 style={styles.titel}>📅 Kalender</h2>
        <div style={styles.verbindenBox}>
          <p>Verbinde deinen Google- und/oder Outlook-Kalender, um Termine anzuzeigen und zu bearbeiten.</p>
          <div style={{ display: "flex", gap: "0.7rem", justifyContent: "center", flexWrap: "wrap", marginTop: "0.5rem" }}>
            <button style={styles.verbindenBtn} onClick={googleVerbinden}>Mit Google verbinden</button>
            <button style={{ ...styles.verbindenBtn, backgroundColor: "#0078d4" }} onClick={outlookVerbinden}>Mit Outlook verbinden</button>
          </div>
          {ladeFehler && <p style={styles.fehler}>{ladeFehler}</p>}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <div ref={stickyRef} style={styles.stickyKopf}>
        <div style={styles.kopfzeile}>
          <h2 style={styles.titel}>📅 Kalender</h2>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {googleToken
              ? <button style={styles.trennenBtn} onClick={googleTrennen} title="Google trennen">🔌 Google</button>
              : <button style={styles.verbindenKleinBtn} onClick={googleVerbinden}>+ Google</button>}
            {msToken
              ? <button style={styles.trennenBtn} onClick={outlookTrennen} title="Outlook trennen">🔌 Outlook</button>
              : <button style={{ ...styles.verbindenKleinBtn, backgroundColor: "#0078d4" }} onClick={outlookVerbinden}>+ Outlook</button>}
            <button style={styles.jetztBtn} onClick={zuJetztSpringen}>⏱️ Jetzt</button>
            <button style={styles.neuBtn} onClick={neuerTermin}>+ Neuer Termin</button>
          </div>
        </div>

        {ladeFehler && <p style={styles.fehler}>{ladeFehler}</p>}

        {/* Kalenderauswahl */}
        {kalenderListe.length > 0 && (
          <div style={styles.kalenderFilter}>
            {kalenderListe.map((k) => (
              <label key={k.id} style={styles.kalenderLabel}>
                <input type="checkbox" checked={!!ausgewaehlteKalender[k.id]} onChange={() => kalenderToggle(k.id)} />
                <span style={{ ...styles.kalenderPunkt, backgroundColor: k.backgroundColor }}></span>
                {k.summary}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Formular */}
      {formOffen && (
        <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && setFormOffen(false)}>
          <div style={styles.modal}>
            <div style={styles.modalKopf}>
              <strong>{bearbeitenTermin ? "Termin bearbeiten" : "Neuer Termin"}</strong>
              <button style={styles.schliessenBtn} onClick={() => setFormOffen(false)}>✕</button>
            </div>
            <form onSubmit={terminSpeichern}>
              <div style={styles.feldZeile}>
                <label style={styles.label}>Titel *</label>
                <input style={styles.input} value={formular.titel} onChange={(e) => setFormular((f) => ({ ...f, titel: e.target.value }))} autoFocus />
              </div>
              {!bearbeitenTermin && (
                <div style={styles.feldZeile}>
                  <label style={styles.label}>Kalender</label>
                  <select style={styles.input} value={formular.kalenderId} onChange={(e) => setFormular((f) => ({ ...f, kalenderId: e.target.value }))}>
                    {kalenderListe.map((k) => <option key={k.id} value={k.id}>{k.quelle === "google" ? "📅" : "📧"} {k.summary}</option>)}
                  </select>
                </div>
              )}
              <div style={styles.feldZeile}>
                <label style={styles.label}>Datum</label>
                <input style={styles.input} type="date" value={formular.datum} onChange={(e) => setFormular((f) => ({ ...f, datum: e.target.value }))} />
              </div>
              <div style={styles.datumZeile}>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Von</label>
                  <input style={styles.input} type="time" value={formular.startZeit} onChange={(e) => setFormular((f) => ({ ...f, startZeit: e.target.value }))} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Bis</label>
                  <input style={styles.input} type="time" value={formular.endZeit} onChange={(e) => setFormular((f) => ({ ...f, endZeit: e.target.value }))} />
                </div>
              </div>
              <div style={styles.feldZeile}>
                <label style={styles.label}>Ort</label>
                <input style={styles.input} value={formular.ort} onChange={(e) => setFormular((f) => ({ ...f, ort: e.target.value }))} />
              </div>
              <div style={styles.feldZeile}>
                <label style={styles.label}>Beschreibung</label>
                <textarea style={{ ...styles.input, resize: "vertical", minHeight: "60px" }} value={formular.beschreibung} onChange={(e) => setFormular((f) => ({ ...f, beschreibung: e.target.value }))} />
              </div>
              <div style={styles.modalAktionen}>
                {bearbeitenTermin && (
                  <button type="button" style={styles.loeschBtn} onClick={() => { terminLoeschen(bearbeitenTermin); setFormOffen(false); }}>🗑️ Löschen</button>
                )}
                <button type="submit" style={styles.speichernBtn}>💾 Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Scrollbarer Terminbereich */}
      <div ref={containerRef} style={styles.scrollBereich}>
        {termine.length === 0 && <p style={styles.leer}>Keine Termine im Zeitraum.</p>}
        <div style={styles.liste}>
          {termine.map((t, i) => (
            <div key={`${t.quelle}-${t.id}`} ref={(el) => { heuteRef.current[i] = el; }} style={styles.terminKarte} onClick={() => terminBearbeiten(t)}>
              <span style={{ ...styles.terminFarbe, backgroundColor: t.kalenderFarbe || "#1a73e8" }}></span>
              <div style={{ flex: 1 }}>
                <div style={styles.terminTitel}>{t.summary || "(Ohne Titel)"}</div>
                <div style={styles.terminZeit}>{formatDatum(t)}{t.location ? ` · ${t.location}` : ""}</div>
              </div>
              <button style={styles.iconBtn} onClick={(e) => { e.stopPropagation(); terminLoeschen(t); }}>🗑️</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrapper: { display: "flex", flexDirection: "column", height: "100%", width: "100%" },
  unverbundenSeite: { padding: "clamp(0.75rem, 2.5vw, 2rem)", width: "100%", boxSizing: "border-box" },
  stickyKopf: { flexShrink: 0, padding: "1.5rem 1.5rem 0.8rem", backgroundColor: "#f0f2f5", zIndex: 5, boxShadow: "0 2px 6px rgba(0,0,0,0.06)" },
  scrollBereich: { flex: 1, overflowY: "auto", padding: "0.8rem 1.5rem 1.5rem" },
  kopfzeile: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" },
  titel: { margin: 0, color: "#1a3558" },
  neuBtn: { padding: "0.5rem 1.1rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600" },
  trennenBtn: { padding: "0.5rem 0.7rem", backgroundColor: "#f0f2f5", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem" },
  verbindenKleinBtn: { padding: "0.5rem 0.7rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem" },
  jetztBtn: { padding: "0.5rem 0.9rem", backgroundColor: "#f0f2f5", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontWeight: "600" },
  verbindenBox: { backgroundColor: "white", borderRadius: "8px", padding: "2rem", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  verbindenBtn: { padding: "0.7rem 1.5rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "1rem" },
  fehler: { color: "#c62828", fontSize: "0.85rem", marginTop: "0.5rem" },
  kalenderFilter: { display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem", backgroundColor: "white", padding: "0.7rem 1rem", borderRadius: "6px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  kalenderLabel: { display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.88rem", cursor: "pointer" },
  kalenderPunkt: { width: "10px", height: "10px", borderRadius: "50%", display: "inline-block" },
  leer: { color: "#999", textAlign: "center", marginTop: "2rem" },
  liste: { display: "flex", flexDirection: "column", gap: "0.5rem" },
  terminKarte: { display: "flex", alignItems: "center", gap: "0.7rem", backgroundColor: "white", padding: "0.7rem 0.9rem", borderRadius: "6px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", cursor: "pointer" },
  terminFarbe: { width: "8px", height: "36px", borderRadius: "4px", flexShrink: 0 },
  terminTitel: { fontWeight: "600", color: "#1a3558", fontSize: "0.95rem" },
  terminZeit: { fontSize: "0.8rem", color: "#777", marginTop: "0.1rem" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "0.95rem" },
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "2rem", overflowY: "auto" },
  modal: { backgroundColor: "white", borderRadius: "10px", padding: "1.5rem", width: "440px", maxWidth: "95vw", boxShadow: "0 8px 30px rgba(0,0,0,0.2)", marginBottom: "2rem" },
  modalKopf: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.2rem" },
  schliessenBtn: { background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer" },
  feldZeile: { marginBottom: "0.8rem" },
  label: { display: "block", fontSize: "0.82rem", color: "#555", marginBottom: "0.25rem", fontWeight: "500" },
  input: { width: "100%", padding: "0.5rem 0.7rem", border: "1px solid #ddd", borderRadius: "6px", fontSize: "0.95rem", boxSizing: "border-box", fontFamily: "sans-serif" },
  datumZeile: { display: "flex", gap: "1rem", marginBottom: "0.8rem" },
  modalAktionen: { display: "flex", gap: "0.6rem", marginTop: "1rem" },
  speichernBtn: { flex: 1, padding: "0.7rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.95rem", fontWeight: "600" },
  loeschBtn: { padding: "0.7rem 1rem", backgroundColor: "#fce8e6", color: "#c62828", border: "1px solid #f5c6c2", borderRadius: "6px", cursor: "pointer", fontSize: "0.9rem" },
};
